import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BasePrimeForm from "./BasePrimeForm";
import AmountInput from "../../../components/Utils/AmountInput";

const PrimeImposable = ({ id, prime, setPrime, closeEdit }) => {
    const { t } = useTranslation();
    const [typeValeur, setTypeValeur] = useState('brut');
    const [showModal, setShowModal] = useState(false);
    const [modalSteps, setModalSteps] = useState([]);
    const [isProratise, setIsProratise] = useState(false);
    const [isAdvanced, setIsAdvanced] = useState(false);

    useEffect(() => {
        if (prime.category) {
            // Déterminer le type de valeur et le statut proratisé basé sur la catégorie
            if (prime.category === "BRUT") {
                setTypeValeur('brut');
                setIsProratise(false);
            } else if (prime.category === "CBP") {
                setTypeValeur('brut');
                setIsProratise(true);
            } else if (prime.category === "PANEX") {
                setTypeValeur('net');
                setIsProratise(false);
            } else if (prime.category === "PN") {
                setTypeValeur('net');
                setIsProratise(false);
            } else if (prime.category === "PNP") {
                setTypeValeur('net');
                setIsProratise(true);
            }
        }
    }, [prime.category]);

    useEffect(() => {
        // Initialiser les valeurs par défaut pour amount_mode et base_type
        if (!prime.amount_mode) {
            setPrime(p => ({ ...p, amount_mode: 'fixed' }));
        }
        if (!prime.base_type) {
            setPrime(p => ({ ...p, base_type: 'wage' }));
        }
        if (!prime.base_rate) {
            setPrime(p => ({ ...p, base_rate: 100.0 }));
        }
        if (!prime.base_multiplier) {
            setPrime(p => ({ ...p, base_multiplier: 1.0 }));
        }
    }, []);

    const updatePrimeCategory = (typeVal, proratise, montantType = null) => {
        let category;
        
        if (typeVal === 'brut') {
            category = proratise ? 'CBP' : 'BRUT';
        } else { 
            if (proratise) {
                category = 'PNP';
            } else {
                category = (montantType === 'received') ? 'PANEX' : 'PN';
            }
        }
        
        setPrime(p => ({ ...p, category }));
    };

    const handleTypeValeurChange = ({ target: { value } }) => {
        setTypeValeur(value);
        updatePrimeCategory(value, isProratise);
    };

    const handleProratiseChange = ({ target: { checked } }) => {
        setIsProratise(checked);
        updatePrimeCategory(typeValeur, checked);
    };

    const handleMontantChange = (newValue) => {
        setPrime(p => ({
            ...p,
            valeur: newValue
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPrime(p => ({ ...p, [name]: value }));
    };

    const handleMontantStatutChange = ({ target: { value } }) => {
        updatePrimeCategory(typeValeur, isProratise, value);
    };

    const handleAmountModeChange = ({ target: { value } }) => {
        setPrime(p => ({ 
            ...p, 
            amount_mode: value,
            // Réinitialiser la valeur si on passe à pourcentage
            ...(value === 'computed' ? { valeur: 0 } : {})
        }));
    };

    const handleBaseTypeChange = ({ target: { value } }) => {
        setPrime(p => ({ ...p, base_type: value }));
    };

    const handleBaseRateChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setPrime(p => ({ ...p, base_rate: value }));
    };

    const handleBaseMultiplierChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setPrime(p => ({ ...p, base_multiplier: value }));
    };

    const handleModeToggle = (checked) => {
        setIsAdvanced(checked);
        // En mode simplifié, on revient au montant fixe
        if (!checked) {
            setPrime(p => ({ ...p, amount_mode: 'fixed' }));
        }
    };

    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    const onBeforeSave = async (primeToSave) => {
        if (typeValeur !== 'net') {
            if (!primeToSave.category) {
                primeToSave.category = "BRUT";
            }
            return primeToSave;
        }

        const steps = [
            { text: t('primeImposable.step1'), status: "pending" },
            { text: t('primeImposable.step2'), status: "pending" },
            { text: t('primeImposable.step3'), status: "pending" },
        ];
        setModalSteps(steps);
        setShowModal(true);

        const updateStep = (index, status, newText) => {
            setModalSteps(prev => prev.map((step, i) => i === index ? { ...step, status, text: newText || step.text } : step));
        };

        await delay(400);
        updateStep(0, "done");

        await delay(400);
        updateStep(1, "done", t('primeImposable.step2Done'));

        await delay(200);
        updateStep(2, "done");

        setModalSteps(prev => [...prev, { text: t('primeImposable.allDone'), status: "done" }]);

        await delay(200);
        setShowModal(false);

        return primeToSave;
    };

    return (
        <>
            <BasePrimeForm
                id={id}
                prime={prime}
                setPrime={setPrime}
                closeEdit={closeEdit}
                title={t('primeImposable.title')}
                onBeforeSave={onBeforeSave}
                isAdvanced={isAdvanced}
                onAdvancedToggle={handleModeToggle}
            >
                <div className="form-control">
                    <label className="label"><span className="label-text">{t('primeImposable.name')}</span></label>
                    <input type="text" className="input input-bordered w-1/2" placeholder={t('primeImposable.primeName')} name="name" value={prime.name || ''} onChange={handleChange} required />
                    <p className="mt-1 text-sm text-gray-500">{t('primeImposable.nameHelp')}</p>
                </div>

                {isAdvanced && (
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeImposable.position')}</span></label>
                        <input type="text" className="input input-bordered w-1/2" name="position" placeholder={t('primeImposable.position')} value={prime.position || ''} onChange={handleChange} />
                        <p className="mt-1 text-sm text-gray-500">{t('primeImposable.positionHelp')}</p>
                    </div>
                )}

                {isAdvanced && (
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeImposable.calculationMode')}</span></label>
                        <select className="select select-bordered w-1/2" value={prime.amount_mode || 'fixed'} onChange={handleAmountModeChange} disabled={!!id}>
                            <option value='fixed'>{t('primeImposable.fixedAmount')}</option>
                            <option value='computed'>{t('primeImposable.percentage')}</option>
                        </select>
                        <p className="mt-1 text-sm text-gray-500">{t('primeImposable.calculationModeHelp')}</p>
                    </div>
                )}

                {isAdvanced && prime.amount_mode === 'computed' && (
                    <>
                        <div className="form-control">
                            <label className="label"><span className="label-text">{t('primeImposable.calculationBase')}</span></label>
                            <select className="select select-bordered w-1/2" value={prime.base_type || 'wage'} onChange={handleBaseTypeChange} disabled={!!id}>
                                <option value='wage'>{t('primeImposable.baseSalary')}</option>
                                <option value='brut'>{t('primeImposable.gross')}</option>
                            </select>
                            <p className="mt-1 text-sm text-gray-500">{t('primeImposable.calculationBaseHelp')}</p>
                        </div>
                        <div className="form-control">
                            <label className="label"><span className="label-text">{t('primeImposable.rate')}</span></label>
                            <input
                                type="number"
                                className="input input-bordered w-1/2"
                                value={prime.base_rate || 100.0}
                                onChange={handleBaseRateChange}
                                step="0.01"
                                min="0"
                                max="100"
                                required
                            />
                            <p className="mt-1 text-sm text-gray-500">{t('primeImposable.rateHelp')}</p>
                        </div>
                        <div className="form-control">
                            <label className="label"><span className="label-text">{t('primeImposable.multiplier')}</span></label>
                            <input
                                type="number"
                                className="input input-bordered w-1/2"
                                value={prime.base_multiplier || 1.0}
                                onChange={handleBaseMultiplierChange}
                                step="0.01"
                                min="0"
                                required
                            />
                            <p className="mt-1 text-sm text-gray-500">{t('primeImposable.multiplierHelp')}</p>
                        </div>
                    </>
                )}

                {(!isAdvanced || prime.amount_mode !== 'computed') && (
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeImposable.amount')}</span></label>
                        <AmountInput
                            value={prime.valeur}
                            onChange={handleMontantChange}
                            className="input input-bordered w-1/2"
                            required
                        />
                        <p className="mt-1 text-sm text-gray-500">{t('primeImposable.amountHelp')}</p>
                    </div>
                )}

                <div className="form-control">
                    <label className="label"><span className="label-text">{t('primeImposable.amountType')}</span></label>
                    <select className="select select-bordered w-1/2" value={typeValeur} onChange={handleTypeValeurChange} disabled={!!id}>
                        <option value='brut'>{t('primeImposable.grossAmount')}</option>
                        <option value='net'>{t('primeImposable.netAmount')}</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-500"><span className="text-red-500">*</span>{typeValeur === 'brut' ? ` ${t('primeImposable.beforeDeductions')}` : ` ${t('primeImposable.afterDeductions')}`}</p>
                </div>
                <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-2">
                        <input
                            type="checkbox"
                            className="checkbox"
                            checked={isProratise}
                            onChange={handleProratiseChange}
                            disabled={!!id}
                        />
                        <span className="label-text">{t('primeImposable.prorated')}</span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">{t('primeImposable.proratedHelp')}</p>
                </div>
                {isAdvanced && (
                    <div className="form-control">
                        <label className="label cursor-pointer justify-start gap-2">
                            <input 
                                type="checkbox" 
                                className="checkbox" 
                                name="maintain_during_leave"
                                checked={!!prime.maintain_during_leave}
                                onChange={e => setPrime(p => ({ ...p, maintain_during_leave: e.target.checked }))}
                            />
                            <span className="label-text">{t('primeImposable.maintainDuringLeave')}</span>
                        </label>
                        <p className="mt-1 text-sm text-gray-500">{t('primeImposable.maintainDuringLeaveHelp')}</p>
                    </div>
                )}
                {(typeValeur === 'net' && !isProratise) && (
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeImposable.amountStatus')}</span></label>
                        <select
                            className="select select-bordered w-1/2"
                            value={prime.category === "PANEX" ? "received" : "added_to_salary"}
                            onChange={handleMontantStatutChange}
                            disabled={!!id}
                        >
                            <option value='received'>{t('primeImposable.alreadyReceived')}</option>
                            <option value='added_to_salary'>{t('primeImposable.addedToSalary')}</option>
                        </select>
                        <p className="mt-1 text-sm text-gray-500">{t('primeImposable.amountStatusHelp')}</p>
                    </div>
                )}
            </BasePrimeForm>

            {showModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">{t('primeImposable.processingTitle')}</h3>
                        <div className="mt-4 space-y-4">
                            {modalSteps.map((step, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-6 h-6">
                                        {step.status === 'pending' && <span className="loading loading-spinner"></span>}
                                        {step.status === 'done' && (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6 text-success" fill="none" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        )}
                                    </div>
                                    <span>{step.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PrimeImposable;